import React from "react";
import { Box, Container, Typography, Grid, Button, Stack } from "@mui/material";
import { styled } from "@mui/system";

// Wrapper sekcji
const SectionWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  paddingTop: 100,
  paddingBottom: 100,
}));

// Nagłówek z podkreśleniem
const UnderlinedHeading = styled("span")({
  position: "relative",
  zIndex: 1,
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    zIndex: "-1",
    right: 0,
    bottom: 0,
    width: "30%",
    height: "30px",
    backgroundImage: "url(/img/blueunderline.svg)",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    backgroundPosition: "bottom center",
  },
});

// Pojedynczy box pakietu
const PackageBox = styled(Box)(({ theme, variant }) => {
  const isPrimary = variant === "primary";
  const isEnterprise = variant === "enterprise";

  return {
    position: "relative",
    padding: 20,
    paddingTop: isPrimary ? 26 : 20,
    paddingBottom: isPrimary ? 26 : 20,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minHeight: isPrimary ? 455 : 350,

    // tło: primary | enterprise | bordered
    backgroundColor: isPrimary
      ? theme.palette.primary.main
      : isEnterprise
        ? "#fff"
        : theme.palette.turquoise.main,

    // obramowanie tylko dla bordered
    border: isEnterprise ? `1px solid ${theme.palette.turquoise.main}` : "none",

    // wzór (pattern) jako overlay dla primary i enterprise
    ...(isPrimary && {
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage: "url(/pattern.svg)",
        backgroundRepeat: "repeat",
        backgroundSize: "contain",
        opacity: 0.1,
        zIndex: 0,
      },
    }),
    ...(isEnterprise && {
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage: "url(/pattern1.svg)",
        backgroundRepeat: "repeat",
        backgroundSize: "contain",
        opacity: 0.5,
        zIndex: 0,
      },
    }),
    // dekoracja po bokach:
    // - dla primary: packageafter.svg pod spodem
    ...(isPrimary && {
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: -35,
        left: 0,
        width: "106%",
        height: 35,
        backgroundImage: "url(/img/packageafter.svg)",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      },
    }),

    // - dla enterprise: packagebefore.svg nad górą boxa
    ...(isEnterprise && {
      "&::after": {
        // używamy ::after, można też ::before w zależności od potrzeb
        content: '""',
        position: "absolute",
        top: -25,
        left: -5,
        width: "103%",
        height: 25,
        backgroundImage: "url(/img/packagebefore.svg)",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      },
    }),
    ...(!isPrimary &&
      !isEnterprise && {
        "&::after": {
          // używamy ::after, można też ::before w zależności od potrzeb
          content: '""',
          position: "absolute",
          top: -25,
          left: -5,
          width: "103%",
          height: 25,
          backgroundImage: "url(/img/packagebefore1.svg)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        },
      }),
    // treść zawsze ponad pseudo-elementami
    "& > *": {
      position: "relative",
      zIndex: 1,
    },
  };
});

// Nota pod pakietami
const FooterNote = styled(Box)(({ theme }) => ({
  width: "40%",
  height: "160px",
  marginLeft: "auto",
  backgroundImage: "url(/img/footernote.png)",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",

  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  alignItems: "flex-end",
  color: "#fff",
  fontSize: "14px",
  padding: 20,
  textAlign: "right",

  "& p": {
    margin: 0,
    lineHeight: 1.5,
  },

  // responsywność: na mobile full width
  [theme.breakpoints.down("sm")]: {
    width: "96%",
    marginLeft: 0, // usuń przesunięcie na mobile
    padding: "12px 16px", // ewentualnie dopasuj padding
  },
}));

const Section7 = () => {
  const items = [
    "Objętości i powierzchnie murów",
    "Objętości elementów żelbetowych",
    "Zestawienie otworów okiennych",
    "Zestawienie otworów drzwiowych",
    "Pobieranie raportu i tabel",
    "Poprawianie jakości obrazów",
    "Pobieranie modelu IFC",
  ];

  return (
    <SectionWrapper
      component="section"
      id="package"
      position="relative"
      sx={{ scrollMarginTop: "100px" }}
    >
      <Container maxWidth="xl">
        <Box
          component="img"
          src="/img/bgpicture3.png"
          alt="bg watermark"
          sx={{
            position: "absolute",
            left: 0,
            bottom: "-5%",
            width: "48%",
            zIndex: 0,
          }}
        />
        <Typography
          variant="h2"
          align="center"
          sx={{
            fontSize: { xs: "28px", md: "54px" },
            fontWeight: "bold",
            color: "primary.main",
            mb: 3,
          }}
        >
          <UnderlinedHeading>Wybierz swój plan!</UnderlinedHeading>
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{ fontSize: "18px", mb: 6 }}
        >
          Wybierz rozwiązanie dla Twojej firmy z dostępnych pakietów
        </Typography>

        <Grid container spacing={4} sx={{ alignItems: "flex-start", mt: 22 }}>
          {/* Pierwszy pakiet */}
          <Grid item size={{ xs: 12, md: 4 }} sx={{ mb: { xs: 8, md: 0 } }}>
            <PackageBox variant="bordered">
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: "bold",
                  color: "primary.main",
                  mb: 1,
                }}
              >
                Standard
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "primary.main",
                  textAlign: "center",
                  mb: 1,
                }}
              >
                Dla profesjonalistów, którzy okazjonalnie
                <br />
                potrzebują wykonać przedmiar lub model.
              </Typography>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: "bold",
                  color: "primary.main",
                  mb: 1,
                }}
              >
                {/* 120 zł/miesiąc netto */}
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: "primary.main",
                  mb: 2,
                }}
              >
                {/* Liczba tokenów : 8* */}
              </Typography>
              <Stack spacing={1} sx={{ width: "100%", mb: 3, mt: 0, gap: 0 }}>
                {items.map((text, i) => {
                  const isLastTwo = i >= items.length - 2;
                  const iconSrc = isLastTwo
                    ? "/img/listicon2.svg"
                    : "/img/listicon.svg";

                  return (
                    <Box
                      key={text}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: 16,
                        color: "primary.main",
                      }}
                    >
                      <span>{text}</span>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {/* Tylko jedna ikona, wybierana na podstawie isLastTwo */}
                        <Box
                          component="img"
                          src={iconSrc}
                          alt="icon"
                          sx={{ width: 24, height: 24 }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>

              <Button
                component="a"
                href="https://app.bimetria.com"
                target="_blank"
                variant="contained"
                size="large" // większy rozmiar
                sx={{
                  alignSelf: "center", // wyśrodkowanie w flex‐boxie
                  backgroundColor: "primary.main",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: { xs: "16px", md: "18px" }, // większa czcionka
                  px: { xs: 4, md: 3 }, // szersze paddingi
                  py: { xs: 1.5, md: 1 }, // wyższe paddingi
                }}
              >
                Wypróbuj za darmo
              </Button>
            </PackageBox>
          </Grid>

          {/* Drugi pakiet */}
          <Grid
            item
            size={{ xs: 12, md: 4 }}
            sx={{ mt: "-7%", mb: { xs: 8, md: "0%" } }}
          >
            <PackageBox variant="primary">
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "100%",
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "error.main",
                    mb: 0,
                    textAlign: "center",
                  }}
                >
                  Najczęściej wybierany
                </Typography>
                <Typography
                  sx={{
                    fontSize: 26,
                    fontWeight: "bold",
                    color: "#fff",
                    mb: 0,
                    textAlign: "center",
                  }}
                >
                  Professional
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 400,
                    color: "#fff",
                    textAlign: "center",
                    mb: 1,
                  }}
                >
                  Dla profesjonalistów, którzy regularnie
                  <br />
                  pozyskują informacje z dwuwymiarowych
                  <br />
                  projektów.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 26,
                    fontWeight: "bold",
                    color: "error.main",
                    mb: 0,
                    textAlign: "center",
                  }}
                >
                  {/* 240 zł/miesiąc netto */}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 500,
                    color: "#fff",
                    mb: 0,
                    textAlign: "center",
                  }}
                >
                  {/* Liczba tokenów : 16* */}
                </Typography>
                <Stack spacing={1} sx={{ width: "100%", mb: 2, mt: 0, gap: 0 }}>
                  {items.map((text) => (
                    <Box
                      key={text}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: 16,
                        color: "#fff",
                      }}
                    >
                      <span>{text}</span>
                      <Box
                        sx={{ width: 24, height: 24 }}
                        component="img"
                        src="/img/listiconred.svg"
                        alt="icon"
                      />
                    </Box>
                  ))}
                </Stack>
                <Button
                  component="a"
                  href="https://app.bimetria.com"
                  target="_blank"
                  variant="contained"
                  size="large" // większy rozmiar
                  sx={{
                    alignSelf: "center", // wyśrodkowanie w flex‐boxie
                    backgroundColor: "turquoise.main",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: { xs: "16px", md: "18px" }, // większa czcionka
                    px: { xs: 4, md: 3 }, // szersze paddingi
                    py: { xs: 1.5, md: 1 }, // wyższe paddingi
                  }}
                >
                  Wypróbuj za darmo
                </Button>
              </Box>
            </PackageBox>
          </Grid>

          {/* Trzeci pakiet */}
          <Grid item size={{ xs: 12, md: 4 }}>
            <PackageBox variant="enterprise">
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: "bold",
                  color: "primary.main",
                  mb: 1,
                  textAlign: "center",
                }}
              >
                Enterprise
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "primary.main",
                  textAlign: "center",
                  mb: 1,
                }}
              >
                Dla profesjonalistów, których praca polega
                <br />
                na wykonywaniu przedmiarów i modeli
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: "bold",
                  color: "primary.main",
                  mb: 2,
                  mt: 1,
                  textAlign: "center",
                  fontStyle: "italic",
                }}
              >
                Skontaktuj się z nami, a dostosujemy ofertę do Twoich
                potrzeb{" "}
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  color: "primary.main",
                  mb: 2,
                  mt: 1,
                  textAlign: "center",
                }}
              >
                kontakt@bimetria.com{" "}
              </Typography>
              <Stack spacing={1} sx={{ width: "100%", mb: 3, mt: 0, gap: 0 }}>
                {items.map((text, i) => {
                  const isLastTwo = i >= items.length - 2;
                  const iconSrc = isLastTwo
                    ? "/img/listicon3.svg"
                    : "/img/listicon.svg";

                  return (
                    <Box
                      key={text}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: 16,
                        color: "primary.main",
                      }}
                    >
                      <span>{text}</span>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {/* Tylko jedna ikona, wybierana na podstawie isLastTwo */}
                        <Box
                          component="img"
                          src={iconSrc}
                          alt="icon"
                          sx={{ width: 24, height: 24 }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
              <Button
                component="a"
                href="https://app.bimetria.com"
                target="_blank"
                variant="contained"
                size="large" // większy rozmiar
                sx={{
                  alignSelf: "center", // wyśrodkowanie w flex‐boxie
                  backgroundColor: "primary.main",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: { xs: "16px", md: "18px" }, // większa czcionka
                  px: { xs: 4, md: 3 }, // szersze paddingi
                  py: { xs: 1.5, md: 1 }, // wyższe paddingi
                }}
              >
                Wypróbuj za darmo
              </Button>
            </PackageBox>
          </Grid>
        </Grid>

        <FooterNote>
          {/* <p>* 1 token = predykcja i generacja wyników dla powierzchni odpowiadającej 100m2</p> */}
          <p>** doliczane do ceny bazowej w zależności od Twoich potrzeb </p>
          <p>*** w zależności od Twoich potrzeb </p>
        </FooterNote>
      </Container>
    </SectionWrapper>
  );
};

export default Section7;
